import React, { useEffect, useRef, useState } from "react"
import { Box, ButtonBase, Typography } from "@mui/material"

import { dsColorNew } from "@palette"
import { AppCardTitle, AppIdentityEditor, AppInfoButton, AppNote, AppResultsSkeleton } from "@components"
import { useToast } from "@hooks"
import { slaveId, username } from "@signals/user"
import { loadIrisProfile } from "@signals/irisProfile"
import { isIrisUser } from "Layout/PmsAgentWidget"
import {
	changeCount, footerHint, labelsFrom, savedSummary, serverFieldError, toDraft, toPayload, validateDraft,
} from "@components/IdentityEditor/@services/identity"
import { USER_SETTINGS_TABS_ID } from "../@constants"
import { SettingsStickyFooter } from "../@components"
import {
	identity, identityError, identityOptions, identitySavedAt, identityState, identityUser, loadIdentity, registerLeaveGuard, saveIdentity,
} from "../@signals"


// "Saved just now" for this long after a save
const JUST_NOW_MS = 60e3

function AccountTitle({ dirty, subtitle }) {
	return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<AppCardTitle icon="id-card" title="Your account" subtitle={subtitle}/>
		<AppInfoButton title="Identity, in three rules">
			<p><b>The first is “you”.</b> The first brand (or, for a broker, the first site) is who Iris speaks for. The rest are also yours.</p>
			<p><b>Yours, per login.</b> What you save here is the identity of <i>your</i> user; Discard returns to what is in use.</p>
			<p><b>It does not open data.</b> You can only pick sites your account already follows; the identity says which of them are yours.</p>
		</AppInfoButton>
		{dirty && <Box component="span" sx={{
			ml: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', fontSize: 11, fontWeight: 600,
			backgroundColor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber, border: `1px solid ${dsColorNew.noteAmberBorder}`,
		}}>Unsaved changes</Box>}
	</Box>
}

/** Settings › Account: the logged-in user's own identity (or the slave's while a master views it). */
export default function UserSettingsAccountTab() {
	const { showToast } = useToast()
	const [draft, setDraft] = useState(null)
	const [touched, setTouched] = useState(false)
	const [failed, setFailed] = useState(false)
	const [, tick] = useState(0)

	const state = identityState.value
	const saved = identity.value
	const value = draft ?? toDraft(saved)
	const labels = labelsFrom(saved, identityOptions.value)
	const changes = changeCount(saved, value)
	const dirty = changes > 0
	const validation = validateDraft(value)
	const saving = state === 'saving'
	const savedAt = identitySavedAt.value
	const savedJustNow = !!savedAt && Date.now() - savedAt.getTime() < JUST_NOW_MS

	// Whose identity this is: the user, or the slave a master is viewing from the header (the server says)
	const who = identityUser.value?.name || (slaveId.value ? 'the user you are viewing' : username.value)

	// Leaving the tab with changes asks first (`useSettingsTabs`); the guard reads the count of this render
	const changesRef = useRef(0)
	changesRef.current = changes
	useEffect(() => registerLeaveGuard(USER_SETTINGS_TABS_ID.ACCOUNT, { label: 'Account', count: () => changesRef.current }), [])

	// "Saved just now" turns back into the resting line after a minute
	useEffect(() => {
		if (!savedJustNow) return
		const t = setTimeout(() => tick(n => n + 1), JUST_NOW_MS)
		return () => clearTimeout(t)
	}, [savedAt])

	// Editing after a rejected save: the old error described the old draft
	const onChange = (next) => {
		setDraft(next)
		setTouched(true)
		setFailed(false)
	}

	const onDiscard = () => showToast.confirm(
		`The ${changes} edit${changes > 1 ? 's' : ''} since your last save go away. The saved identity stays.`,
		{
			title: 'Discard your changes?', tone: 'danger', submitText: 'Discard',
			onSubmit: () => { setDraft(null); setTouched(false); setFailed(false) },
		}
	)

	const onSave = async () => {
		setTouched(true)
		if (!validation.ok) return
		const ok = await saveIdentity(toPayload(value))
		if (ok) {
			setDraft(null)
			setTouched(false)
			setFailed(false)
			// Iris's profile carries `own` / `compare` and the text built from this identity: refresh it (CU-869fkmt6r)
			if (isIrisUser()) loadIrisProfile({ force: true })
			const summary = savedSummary(identity.value)
			showToast.success(summary ? `Identity saved. ${summary}` : 'Identity saved')
		} else {
			setFailed(true)
			const e = identityError.value
			const field = serverFieldError(e, labels)
			const reason = field?.message || (e?.status === 422 ? e?.message : null)
			const detail = reason ? ` · ${reason.replace(/\.$/, '')}` : ''
			showToast.error(`Could not save your identity · ${e?.status || 'network error'}${detail}. Nothing was changed — your edits are still on screen.`)
		}
	}

	if (state === 'idle' || state === 'loading') return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} aria-busy="true">
		<AccountTitle subtitle="Loading your identity…"/>
		<AppResultsSkeleton rows={3} columns={3} height={180}/>
	</Box>

	if (state === 'error') return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<AccountTitle subtitle={`How Iris knows who you are — saved for ${who}`}/>
		<AppNote severity="error">
			<b>Could not load your identity</b> — {identityError.value?.status || 'network error'}. Nothing was changed; Iris keeps using what was saved.{' '}
			<ButtonBase onClick={() => loadIdentity({ force: true })} sx={{
				fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: dsColorNew.appBlue, verticalAlign: 'baseline',
			}}>Try again</ButtonBase>
		</AppNote>
		<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>Nothing to edit until your identity loads. The other tabs work as usual.</Typography>
	</Box>

	const hint = footerHint({
		saved, dirty, changes, validation, savedJustNow,
		error: failed && dirty ? identityError.value : null,
	})

	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<AccountTitle dirty={dirty} subtitle={<>How Iris knows who you are — saved for <Box component="strong" sx={{ fontWeight: 600, color: dsColorNew.textDark }}>{who}</Box></>}/>
		<AppIdentityEditor
			value={value}
			onChange={onChange}
			labels={labels}
			options={identityOptions.value}
			saved={saved}
			validation={validation}
			serverError={failed ? serverFieldError(identityError.value, labels) : null}
			showErrors={touched}
			disabled={saving}
		/>
		<SettingsStickyFooter
			hint={hint}
			onDiscard={onDiscard}
			discardDisabled={!dirty || saving}
			onSave={onSave}
			saveDisabled={!dirty || !validation.ok || saving}
			saving={saving}
			saveLabel={saving ? 'Saving…' : failed && dirty ? 'Try again' : 'Save changes'}
		/>
	</Box>
}
